import { Space, Popconfirm, message, Skeleton, Table, Popover } from 'antd';
import styled from 'styled-components';
import type { ColumnsType } from 'antd/lib/table';
import { useState, useEffect } from 'react';
import { AxiosError } from 'axios';
import { useQuery } from '@tanstack/react-query';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { PlusOutlined } from '@ant-design/icons';

import { getPaymentMethods, deletePaymentMethod } from 'services/collections';
import PaymentMethodsForm from 'pages/Dialogs/PaymentMethodsForm';

import useTranslation from '../../customHooks/translate';
import { PAYMENT_METHODS } from 'events/types';

const PaymentMethods = () => {
  const translation = useTranslation();
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [paymentMethodToUpdate, setPaymentMethodToUpdate] = useState(null);

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);



  const columns: ColumnsType<any> = [
    {
      title: <span>{translation.paymentMethods.PaymentMethod}</span>,
      align: 'center',
      dataIndex: 'name',
      width: 300,
      key: 'name',
    },
    {
      title: <span>{translation.paymentMethods.Action}</span>,
      align: 'center',
      key: 'actions',
      width: 200,
      render: (_, record) =>
        isMobile ? (
          <Popover
            placement="bottom"
            trigger="click"
            content={
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '20px',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <EditDeleteIcon
                  src="assets/edit-icon.svg"
                  alt="Edit"
                  onClick={() => {
                    setPaymentMethodToUpdate(record);
                    setAddModalOpen(true);
                  }}
                />
                <Popconfirm
                  title={translation.DeleteItem}
                  onConfirm={() => handleDelete(record)}
                  okText="Yes"
                  placement="left"
                  cancelText="No"
                >
                  <EditDeleteIcon
                    src="assets/delete-icon.svg"
                    alt="Delete"
                    // onClick={() => handleDelete(record)}
                  />
                </Popconfirm>
              </div>
            }
          >
            {record.isEditable && (
              <MoreVertIcon
                style={{
                  fontSize: '29px',
                  border: '1px solid #292d32bf',
                  borderRadius: '50%',
                  padding: '4px',
                }}
              />
            )}
          </Popover>
        ) : (
          <Space size="middle">
            {record.isEditable && (
              <>
                <div style={{ cursor: 'pointer' }}>
                  <img
                    className="edit"
                    src="assets/edit-icon.svg"
                    alt="Edit"
                    onClick={() => {
                      setPaymentMethodToUpdate(record);
                      setAddModalOpen(true);
                    }}
                  />
                </div>
                <Popconfirm
                  title={translation.DeleteItem}
                  onConfirm={() => handleDelete(record)}
                  okText="Yes"
                  cancelText="No"
                >
                  <div style={{ cursor: 'pointer' }}>
                    <img
                      className="delete"
                      src="assets/delete-icon.svg"
                      alt="Delete"
                    />
                  </div>
                </Popconfirm>
              </>
            )}
          </Space>
        ),
    },
  ];

  const { status, error, data, isLoading, refetch, isFetching } = useQuery<
    any,
    AxiosError,
    any[]
  >({
    queryKey: [PAYMENT_METHODS],
    queryFn: getPaymentMethods,
    select: (response) => response.data.data.map((d) => ({ ...d, key: d.id })),
    keepPreviousData: true,
    refetchOnMount: 'always',
  });

  const handleDelete = async (record) => {
    try {
      const id = record.id;
      await deletePaymentMethod(id);
      refetch();
      message.success(translation.messages.successDeleted);
    } catch {
      message.error('An error occurred while deleting the payment method');
    }
  };

  const handleSuccess = (action) => {
    refetch();
    message.success(
      action === 'added'
        ? translation.InvoiceLine.SuccessMessage
        : translation.messages.successUpdated
    );
  };

  return (
    <>
      <div className="mb-[20px] flex w-full flex-wrap items-center justify-between gap-[10px]">
        <p className="text-[20px] font-medium text-[#1D1D1D] md:text-[24px]">
          {translation.paymentMethods.PaymentMethods}
        </p>
        <button
          onClick={() => setAddModalOpen(true)}
          className="flex w-auto w-full w-max items-center gap-[8px] rounded-[6px] rounded-[6px] bg-[#466afb] px-[16px] py-[8px] text-[16px] font-normal leading-[16px] text-white md:px-[24px] md:py-[12px]"
        >
          <PlusOutlined />
          <p className="hidden md:block">
            {translation.paymentMethods.AddNewPaymentButton}
          </p>
        </button>
      </div>
      <PaymentMethodsForm
        open={addModalOpen}
        paymentMethodToUpdate={paymentMethodToUpdate}
        handleClose={() => {
          setPaymentMethodToUpdate(null);
          setAddModalOpen(false);
        }}
        onSuccess={handleSuccess}
      />
      <STableWrapper>
        {status === 'loading' ? (
          <Skeleton active />
        ) : status === 'error' ? (
          <span>Error: {error.message}</span>
        ) : (
          <Table
            sticky={{
              offsetHeader: 0,
            }}
            className="custom-bordered"
            bordered
            scroll={{ x: 'max-content' }}
            dataSource={data}
            columns={columns}
            loading={isLoading || isFetching}
            pagination={false}
            rowClassName={(_, i) =>
              i % 2 === 0 ? 'bg-[#466AFB08]' : 'bg-white'
            }
          />
        )}
      </STableWrapper>
    </>
  );
};

const STableWrapper = styled.div`
  margin-top: 24px;
  height: 500px;
  overflow: auto;
`;

const EditDeleteIcon = styled.img`
  cursor: pointer;
  width: 24px;
  height: 24px;
`;

export default PaymentMethods;
